import { useMemo, useState } from "preact/hooks";
import type { CrmData } from "../../types";
import { CompanyStatusBadge } from "../ui/Badge";
import { Avatar } from "../ui/Avatar";
import { companyLastActivity, openOpportunitiesCount, totalPipelineValue } from "../../lib/selectors";
import { formatDate, money } from "../../lib/format";
import { EmptyState } from "../ui/States";

type SortKey = "name" | "industry" | "size" | "country" | "accountOwner" | "openOpportunities" | "pipelineValue" | "lastActivity" | "status";

export function CompaniesTable({
  data,
  onOpenCompany,
  onNewCompany,
}: {
  data: CrmData;
  onOpenCompany: (companyId: string) => void;
  onNewCompany: () => void;
}) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const rows = useMemo(() => {
    return data.companies.map((c) => ({
      company: c,
      openOpportunities: openOpportunitiesCount(c.id, data.opportunities),
      pipelineValue: totalPipelineValue(c.id, data.opportunities),
      lastActivity: companyLastActivity(c.id, data.opportunities, data.activities),
    }));
  }, [data.companies, data.opportunities, data.activities]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter && r.company.status !== statusFilter) return false;
      if (term && !r.company.name.toLowerCase().includes(term) && !r.company.country.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [rows, search, statusFilter]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    copy.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case "name":
          cmp = a.company.name.localeCompare(b.company.name);
          break;
        case "industry":
          cmp = a.company.industry.localeCompare(b.company.industry);
          break;
        case "size":
          cmp = a.company.size.localeCompare(b.company.size);
          break;
        case "country":
          cmp = a.company.country.localeCompare(b.company.country);
          break;
        case "accountOwner":
          cmp = a.company.accountOwner.localeCompare(b.company.accountOwner);
          break;
        case "openOpportunities":
          cmp = a.openOpportunities - b.openOpportunities;
          break;
        case "pipelineValue":
          cmp = a.pipelineValue - b.pipelineValue;
          break;
        case "lastActivity":
          cmp = new Date(a.lastActivity ?? 0).getTime() - new Date(b.lastActivity ?? 0).getTime();
          break;
        case "status":
          cmp = a.company.status.localeCompare(b.company.status);
          break;
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [filtered, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  }

  function header(label: string, key: SortKey) {
    const active = sortKey === key;
    return (
      <th>
        <button type="button" class="table-sort" onClick={() => toggleSort(key)}>
          {label}
          {active && <span class="table-sort__arrow">{sortDir === "asc" ? "▲" : "▼"}</span>}
        </button>
      </th>
    );
  }

  return (
    <div class="page">
      <div class="page__header">
        <div>
          <h1 class="page__title">Companies</h1>
          <p class="page__subtitle">
            {data.companies.length} accounts in your workspace
          </p>
        </div>
        <button type="button" class="btn btn--primary" data-ai-id="create-company" onClick={onNewCompany}>
          + New Company
        </button>
      </div>
      <div class="filters-bar">
        <input
          class="filters-bar__search"
          type="search"
          placeholder="Search company or country…"
          value={search}
          data-ai-id="search"
          onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
        />
        <select value={statusFilter} data-ai-id="filter-status" onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value)}>
          <option value="">All statuses</option>
          <option value="Prospect">Prospect</option>
          <option value="Active Customer">Active Customer</option>
          <option value="At Risk">At Risk</option>
          <option value="Churned">Churned</option>
        </select>
      </div>

      {sorted.length === 0 ? (
        <EmptyState title="No companies found" description="Try a different search term or filter." />
      ) : (
        <div class="table-wrap">
          <table>
            <thead>
              <tr>
                {header("Company", "name")}
                {header("Industry", "industry")}
                {header("Size", "size")}
                {header("Country", "country")}
                {header("Owner", "accountOwner")}
                {header("Open opps", "openOpportunities")}
                {header("Pipeline value", "pipelineValue")}
                {header("Last activity", "lastActivity")}
                {header("Status", "status")}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr
                  key={r.company.id}
                  class="table-row-clickable"
                  data-ai-id={`open-company-${r.company.id}`}
                  onClick={() => onOpenCompany(r.company.id)}
                >
                  <td>
                    <span class="td-company">
                      <Avatar name={r.company.name} size="sm" />
                      {r.company.name}
                    </span>
                  </td>
                  <td class="td-industry">{r.company.industry}</td>
                  <td>{r.company.size}</td>
                  <td>{r.company.country}</td>
                  <td>{r.company.accountOwner}</td>
                  <td class="td-num">{r.openOpportunities}</td>
                  <td class="td-num">{money(r.pipelineValue)}</td>
                  <td>{formatDate(r.lastActivity)}</td>
                  <td>
                    <CompanyStatusBadge status={r.company.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
